<template>
    <div class="block block-rounded">
        <div class="block-header block-header-default">
            <h3 class="block-title">
                <template v-if="salesInvoice.number">
                    #{{ salesInvoice.number }}
                </template>
                <template v-else>
                    Invoice {{ salesInvoice.id }} (draft)
                </template>
            </h3>
            <div class="block-options overflow-hidden" v-if="salesInvoice.number">
                <a class="btn btn-sm btn-alt-info me-1" href="" v-bind:href="this.routes.viewInXeroRoute" target="_blank">
                    <i class="fa fa-fw fa-window-restore"></i> Manage in Xero
                </a>
                <a class="btn btn-sm btn-alt-info me-1" href="" v-bind:href="this.routes.viewInXeroCustomerRoute" target="_blank">
                    <i class="fa fa-fw fa-window-restore"></i> View in Xero
                </a>
                <a class="btn btn-sm btn-alt-info me-1" href="" v-bind:href="this.routes.downloadInvoicePdfRoute">
                    <i class="fa fa-fw fa-download"></i> Download PDF
                </a>
                <classics-purchase-invoices-email class="d-inline" :invoice="salesInvoice" :customer="customer" :routes="this.routes" />
            </div>
            <div class="block-options overflow-hidden" v-else>
                <button class="btn btn-sm btn-alt-secondary me-1" v-on:click="editable = true" v-if="!editable">
                    <i class="fa fa-fw fa-pencil-alt"></i> Edit Lines
                </button>
                <button class="btn btn-sm btn-primary" v-on:click="editable = true; additionalLineItems.push(Object.assign({}, newLineItem))">
                    <i class="fa fa-fw fa-plus"></i> Add Line
                </button>
            </div>
        </div>
        <div class="block-content">
            <div class="p-sm-4">
                <table class="table table-bordered">
                    <thead>
                        <tr>
                            <th class="bg-body-dark align-middle" style="width:150px;"><label for="salesInvoice-datepicker">Invoice Date</label></th>
                            <td>
                                <input type="text" class="form-control js-flatpickers flatpickr-input" id="salesInvoice-datepicker"
                                    placeholder="Y-m-d" data-date-format="d/m/Y" v-model="invoiceDate" :disabled="salesInvoice.number"/>
                            </td>
                        </tr>
                        <tr>
                            <th class="bg-body-dark align-middle"><label for="salesInvoice-terms">Invoice Terms</label></th>
                            <td>
                                <div class="input-group">
                                    <input type="number" min="1" step="1" class="form-control" id="salesInvoice-terms"
                                        placeholder="30" v-model="invoiceDueIn" :disabled="salesInvoice.number"/>
                                    <span class="input-group-text">days</span>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="customer">
                            <th class="bg-body-dark">Name</th>
                            <td>{{ customer.name }}</td>
                        </tr>
                        <tr>
                            <th class="bg-body-dark">Vehicle</th>
                            <td>{{ vehicle.year }} {{ vehicle.make }} {{ vehicle.model }}</td>
                        </tr>
                        <tr>
                            <th class="bg-body-dark">Reg</th>
                            <td>{{ vehicle.reg }}</td>
                        </tr>
                    </thead>
                </table>
                <div class="table-responsive push">
                    <table class="table table-bordered">
                        <thead class="bg-body-dark">
                            <tr>
                                <th style="width: 60px;"></th>
                                <th>Product</th>
                                <th class="text-center" style="width: 90px;">Qty</th>
                                <th class="text-center" style="width: 120px;">Unit</th>
                                <th class="text-center" style="width: 120px;">Amount</th>
                                <th v-if="editable" class="text-end" style="width: 120px;">Tax Rate</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="(lineItem, index) in timeEntryLineItems" :key="'timeEntry' + index">
                                <tr>
                                    <td>
                                        <button class="btn" v-on:click="showPartsLineItem = null;showLineItemTimeEntries = ! showLineItemTimeEntries">
                                            <i
                                                class="fas"
                                                :class="{
                                                    'fa-angle-down': ! showLineItemTimeEntries,
                                                    'fa-angle-up': showLineItemTimeEntries,
                                                }"></i>
                                        </button>
                                    </td>
                                    <td>
                                        <p class="fw-semibold mb-1">{{ lineItem.description }}</p>
                                        <div class="text-muted" v-show="showLineItemTimeEntries" v-html="lineItem.formattedTimeEntries.join('<br>')"></div>
                                    </td>
                                    <template v-if="!editable">
                                        <td class="text-center">
                                            <span class="badge rounded-pill bg-primary">{{ lineItem.quantity }}</span>
                                        </td>
                                        <td class="text-end">&pound;{{ lineItem.unit_price }}</td>
                                        <td class="text-end">&pound;{{ lineItem.net_total }}</td>
                                    </template>
                                    <template v-else>
                                        <td>
                                            <input class="input-cell" type="number" v-model="lineItem.quantity" />
                                        </td>
                                        <td class="text-end">
                                            &pound;
                                            <input class="input-cell" type="number" v-model="lineItem.unit_price" style="width: 70%; text-align: right;" min="0.01"/>
                                        </td>
                                        <td class="text-end">&pound;{{ lineItem.net_total }}</td>
                                    </template>
                                    <td v-if="editable" class="text-end"></td>
                                </tr>
                            </template>
                            <template v-for="(lineItem, index) in partsLineItems" :key="'purchaseInvoice' + index">
                                <tr>
                                    <td>
                                        <button class="btn default" v-on:click="showLineItemTimeEntries = false;showPartsLineItem = (showPartsLineItem === index) ? null : index">
                                            <i
                                                class="fas"
                                                :class="{
                                                    'fa-angle-down': showPartsLineItem !== index,
                                                    'fa-angle-up': showPartsLineItem === index,
                                                }"></i>
                                        </button>
                                    </td>
                                    <td>
                                        <template v-if="!editable">
                                            <p class="fw-semibold mb-1" style="white-space:pre">{{ lineItem.description }}</p>
                                        </template>
                                        <template v-else>
                                            <expanding-textarea v-model="lineItem.description" />
                                        </template>
                                        <div class="text-muted" v-show="showPartsLineItem == index">
                                            <p v-for="row in lineItem.formattedPurchaseInvoices" class="m-0">
                                                {{ row.text }} <a :href="row.link" target="_blank">Open <span style="display:inline-block;transform:scale(-1,1)">↸</span></a>
                                            </p>
                                        </div>
                                    </td>
                                    <template v-if="!editable">
                                        <td class="text-center">
                                            <span class="badge rounded-pill bg-primary">{{ lineItem.quantity }}</span>
                                        </td>
                                        <td class="text-end">&pound;{{ lineItem.unit_price }}</td>
                                        <td class="text-end">&pound;{{ lineItem.net_total }}</td>
                                    </template>
                                    <template v-else>
                                        <td>
                                            <input class="input-cell" type="number" v-model="lineItem.quantity" />
                                        </td>
                                        <td class="text-end">
                                            &pound;
                                            <input class="input-cell" type="number" v-model="lineItem.unit_price" style="width: 70%; text-align: right;" min="0.01"/>
                                        </td>
                                        <td class="text-end">&pound;{{ lineItem.net_total }}</td>
                                    </template>
                                    <td v-if="editable"></td>
                                </tr>
                            </template>
                            <template v-for="(lineItem, index) in additionalLineItems">
                                <template v-if="!editable">
                                    <tr :key="'additional' + index">
                                        <td></td>
                                        <td>
                                            <p class="fw-semibold mb-1" style="white-space:pre">{{ lineItem.description }}</p>
                                            <div class="text-muted"></div>
                                        </td>
                                        <td class="text-center">
                                            <span class="badge rounded-pill bg-primary">{{ lineItem.quantity }}</span>
                                        </td>
                                        <td class="text-end">&pound;{{ lineItem.unit_price }}</td>
                                        <td class="text-end">&pound;{{ lineItem.net_total }}</td>
                                    </tr>
                                </template>
                                <template v-else>
                                    <tr :key="'additional' + index">
                                        <td class="text-center">
                                            <button type="button" class="btn" v-on:click="removeLineItem(index)">
                                                <i class="fas fa-times"></i>
                                            </button>
                                        </td>
                                        <td>
                                            <expanding-textarea v-model="lineItem.description" />
                                        </td>
                                        <td>
                                            <input class="input-cell" type="number" v-model="lineItem.quantity" />
                                        </td>
                                        <td class="text-end">
                                            &pound;
                                            <input class="input-cell" type="number" v-model="lineItem.unit_price" style="width: 70%; text-align: right;" min="0.01"/>
                                        </td>
                                        <td class="text-end">&pound;{{ lineItem.net_total }}</td>
                                        <td v-if="editable" style="text-align: center;">
                                            <select v-model="lineItem.tax_type" class="form-select" style="width:100%;">
                                                <option v-for="activeTaxRate in activeTaxRates" :value="activeTaxRate.tax_type">{{ activeTaxRate.tax_name }}</option>
                                            </select>
                                        </td>
                                    </tr>
                                </template>
                            </template>
                            <template v-for="(lineItem, index) in lineItems.consumables" :key="'consumable' + index">
                                <tr>
                                    <td></td>
                                    <td>
                                        <sales-invoice-line-item-edit-calculation v-if="lineItem.unit_price_calculation && !salesInvoice.number" :line-item="lineItem" :save-route="routes.saveLineItemUnitPriceRoute" />
                                        <p class="fw-semibold mb-1">{{ lineItem.description }}</p>
                                    </td>
                                    <td class="text-center">
                                        <span class="badge rounded-pill bg-primary">{{ lineItem.quantity }}</span>
                                    </td>
                                    <td class="text-end">&pound;{{ lineItem.unit_price }}</td>
                                    <td class="text-end">&pound;{{ lineItem.net_total }}</td>
                                    <td v-if="editable" class="text-end"></td>
                                </tr>
                            </template>
                            <tr>
                                <td colspan="4" class="fw-semibold text-end">Subtotal</td>
                                <td class="text-end">&pound;{{ parseFloat(salesInvoice.net_total).toFixed(2) }}</td>
                                <td v-if="editable"></td>
                            </tr>
                            <tr>
                                <td colspan="4" class="fw-semibold text-end">VAT Due</td>
                                <td class="text-end">&pound;{{ parseFloat(salesInvoice.vat_total).toFixed(2) }}</td>
                                <td v-if="editable"></td>
                            </tr>
                            <tr class="bg-body-dark">
                                <td colspan="4" class="fw-bold text-uppercase text-end">Invoice Total</td>
                                <td class="fw-bold text-end">&pound;{{ (parseFloat(salesInvoice.net_total) + parseFloat(salesInvoice.vat_total)).toFixed(2) }}</td>
                                <td v-if="editable"></td>
                            </tr>
                            <tr v-if="salesInvoice.amount_due > 0" :class="{'bg-danger-light': !salesInvoice.paid_at}">
                                <td colspan="4" class="fw-bold text-uppercase text-end">Amount Due</td>
                                <td class="fw-bold text-end">&pound;{{ salesInvoice.amount_due }}</td>
                                <td v-if="editable"></td>
                            </tr>
                            <tr v-if="salesInvoice.paid_at" :class="{'bg-success-light': salesInvoice.paid_at}">
                                <td colspan="6" class="fw-bold text-uppercase text-center">Invoice fully paid on {{ salesInvoice.paid_at_date }}</td>
                                <td v-if="editable"></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="row">
                    <div class="col-md-6">
                        <div class="mb-4">
                            <label class="form-label">Internal Notes</label>
                            <textarea class="form-control" rows="7" v-model="internalNotes"></textarea>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="mb-4">
                            <label class="form-label">Customer Notes</label>
                            <textarea class="form-control" rows="7" v-model="customerNotes"></textarea>
                        </div>
                    </div>
                </div>
                <div class="text-end">
                    <template v-if="editable || saveable">
                        <button class="btn btn-sm btn-alt-secondary me-1" v-on:click="reset">Reset</button>
                        <button class="btn btn-sm btn-success" v-on:click="save">Save Changes</button>
                    </template>
                    <template v-else-if="! salesInvoice.number">
                        <button class="btn btn-sm btn-success" v-on:click="pushToXero">Send To Xero</button>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import axios from 'axios';
import salesInvoiceLineItemEditCalculation from './editCalculation.vue';
import classicsPurchaseInvoicesEmail from './email.vue';
import ExpandingTextarea from "../../expanding-textarea.vue";

export default {
    props: {
        salesInvoice: Object,
        lineItems: Object,
        activeTaxRates: Array,
        routes: Object,
        customer: Object,
        vehicle: Object
    },
    components: {
        ExpandingTextarea,
        salesInvoiceLineItemEditCalculation,
        classicsPurchaseInvoicesEmail
    },
    data () {
        return {
            newLineItem: {
                description: '',
                unit_price: 0.00,
                quantity: 0,
                tax_type: '',
                net_total: 0,
                vat_total: 0,
                type: '',
                taxRateId: null,
            },
            invoiceId: null,
            partsLineItems: [],
            timeEntryLineItems: [],
            additionalLineItems: [],
            editable: false,
            saveable: false,
            showLineItemTimeEntries: false,
            showPartsLineItem: null,
            internalNotes: '',
            customerNotes: '',
            invoiceDate: null,
            invoiceDueIn: null,
        };
    },
    mounted () {
        this.reset();
        this.invoiceDate = new Date(this.salesInvoice.date).toLocaleDateString();
        this.invoiceDueIn = this.salesInvoice.days_due_in;
    },
    watch: {
        timeEntryLineItems: {
            handler: function () {
                for (let lineItem of this.timeEntryLineItems) {
                    let activeTaxRate = this.activeTaxRates.filter((taxRate) => {
                        return taxRate.tax_type == lineItem.tax_type;
                    })[0];
                    if (activeTaxRate === undefined) {
                        continue;
                    }
                    lineItem.tax_rate = activeTaxRate.tax_percentage / 100;

                    lineItem.net_total = ((lineItem.unit_price > 0) && (lineItem.quantity > 0)) ? parseFloat(lineItem.unit_price * lineItem.quantity).toFixed(2) : 0;
                    lineItem.vat_total = lineItem.net_total * lineItem.tax_rate;
                }
            },
            deep: true
        },
        additionalLineItems: {
            handler: function () {
                for (let lineItem of this.additionalLineItems) {
                    let activeTaxRate = this.activeTaxRates.filter((taxRate) => {
                        return taxRate.tax_type == lineItem.tax_type;
                    })[0];
                    if (activeTaxRate === undefined) {
                        continue;
                    }
                    lineItem.tax_rate = activeTaxRate.tax_percentage / 100;

                    lineItem.net_total = ((lineItem.unit_price > 0) && (lineItem.quantity > 0)) ? parseFloat(lineItem.unit_price * lineItem.quantity).toFixed(2) : 0;
                    lineItem.vat_total = lineItem.net_total * lineItem.tax_rate;
                }
            },
            deep: true
        },
        internalNotes() {
            this.saveable = true;
        },
        customerNotes() {
            this.saveable = true;
        },
        invoiceDate() {
            this.saveable = true;
        },
        invoiceDueIn() {
            this.saveable = true;
        },
    },
    methods: {
        save: function () {
            // check if description is empty and ensure they don't send an empty field. If it is empty or null, throw an error
            if (this.additionalLineItems.filter(data => data.description.length < 1).length > 0) {
                // tell the user they can't send an empty description and prevent them from adding extra lines to make the invoice 'look better'
                Swal.fire({
                    icon: 'error',
                    title: 'Line item description cannot be empty',
                });
                return;
            }

            axios.post(this.routes.addLineItemRoute, {
                timeEntryLineItems: this.timeEntryLineItems,
                partsLineItems: this.partsLineItems,
                additionalLineItems: this.additionalLineItems,
                internalNotes: this.internalNotes,
                customerNotes: this.customerNotes,
                invoiceDate: this.invoiceDate,
                invoiceDueIn: this.invoiceDueIn,
            }).then((response) => {
                // tell the user they've done a good job.
                Swal.fire({
                    position: 'center',
                    icon: 'success',
                    title: 'The invoice has been successfully updated',
                    showConfirmButton: true,
                }).then(() => location.reload());
            }).catch(() => {
                Swal.fire({
                    icon: 'error',
                    title: 'Unable to save invoice',
                });
            });
        },
        removeLineItem: function (key) {
            this.additionalLineItems.splice(key, 1);
        },
        pushToXero: function () {
            Swal.fire({
                    title: 'Are you sure you want to send this invoice to Xero?',
                    text: 'This cannot be undone and you will not be able to edit the invoice here afterwards.',
                    position: 'center',
                    icon: 'success',
                    showConfirmButton: true,
                    showCancelButton: true,
                }).then((result) => {
                        if (result.isConfirmed) {
                            location.replace(this.routes.sendInvoiceToXeroRoute);
                        }
                    });
        },
        reset: function () {
            // stringify to break reference
            this.timeEntryLineItems = JSON.parse(JSON.stringify(this.lineItems.timeEntries)) || [];
            this.partsLineItems = JSON.parse(JSON.stringify(this.lineItems.purchaseInvoices)) || [];
            this.additionalLineItems = JSON.parse(JSON.stringify(this.lineItems.additional)) || [];
            this.editable = false;

            this.internalNotes = this.salesInvoice.internal_notes;
            this.customerNotes = this.salesInvoice.customer_notes;
            setTimeout(() => {
                this.saveable = false;
            }, 0);
        },
    }
}
</script>
<style scoped>
    .input-cell {
        min-width: 10%;
        max-width: 100%;
        width: 100%;
        border: none;
    }
    .flt-r {
        float:right;
    }
    .flt-padding-r{
        float: right;
        padding-right: 5px;
    }
</style>
